import React from 'react';
import * as IoC from 'typescript-ioc';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../../infrastructure/sub-windows';
import { ConnectionType, IChildConnectionDTO, IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../../api/connections.dao';
import { EDIT_CONNECTION_DETAILS } from '../meeting-connections-form.texts';
import { action, observable } from 'mobx';
import { MeetingConnectionsAggregateRoot } from '../../../../../../modules/connections/meeting/meeting-connections.aggregate-root';
import { FormattedMessage, IntlShape } from 'react-intl';
import { UniqueDialInChildConnectionForm } from '../../../shared/child-connection-form/unique-dial-in/unique-dial-in-child-connection-form';
import { ChildConnectionFormProps } from '../../../shared/child-connection-form/types';
import { UniqueJoiningLinkChildConnectionForm } from '../../../shared/child-connection-form/unique-joining-link/unique-joining-link-child-connection-form';
import { ATTENDEE_WILL_NO_HAVE_UNIQUE_CONNECTION_TEXT, DELETE_THIS_CONNECTION_TEXT, UNIQUE_CONNECTION_DETAILS_UPDATED_TEXT } from '../../connections.texts';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { DELETE_BOTH_TEXT, DELETE_CONNECTION_TEXT, FORM_ARE_YOU_SURE_TO_TEXT, YES_DELETE_TEXT } from '../../../../../_blueprints/conections-templates/connections-template-form/connection-template-form.texts';
import { Space } from '../../../../../../constants/html-character-entities';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { PanelistLinkChildConnectionForm } from '../../../shared/child-connection-form/panelist-link-connection/panelist-link-child-connection-form';
import { alertPanelistLinkConnectionMessage, panelistDeletionToastMessage, panelistLinkWarningMessage } from '../../../../../../constants/panelist-link-connection.constants';


export class ChildConnectionsViewModel {
    private static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private subWindowMap: { [id: string]: ISubWindowFacade } = {};

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly meetingId: number,
        private readonly meetingConnectionsDataFormVM: MeetingConnectionsAggregateRoot,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {}

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    @action.bound
    public openChildConnectionEditModal(childConnection: IChildConnectionDTO, parentConnectionId: number): void {
        const openedModal = this.subWindowMap[childConnection.joinMethodId];
        if (openedModal && openedModal.getStatus() !== SubWindowStatus.Closed) {
            openedModal.maximize();
            return;
        }

        const parentConnectionDetails = this.meetingConnectionsDataFormVM.getMeetingConnectionById(parentConnectionId);

        this.subWindowMap[childConnection.joinMethodId] = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(EDIT_CONNECTION_DETAILS),
            componentToHost: this.getChildConnectionComponentByType(childConnection.type),
            componentToHostProps: {
                accountId: this.accountId,
                eventId: this.eventId,
                childConnection: childConnection,
                parentConnectionDetails: parentConnectionDetails,
                onChildConnectionUpdated: this.onChildConnectionUpdated.bind(this)
            }
        });
    }

    public openDeleteChildConnectionConfirmation = (childConnection: IChildConnectionDTO, parentConnectionId: number): void => {
        const isPanelistChild = childConnection.type === ConnectionType.PanelistLink;
        const message = isPanelistChild
            ? (
                <span className="alert-message">
                    <FormattedMessage {...FORM_ARE_YOU_SURE_TO_TEXT} />
                    <Space />
                    <span className="alert-message--danger">
                        {alertPanelistLinkConnectionMessage}
                    </span>
                    <br />
                    {panelistLinkWarningMessage}
                </span>
            )
            : (
                <span className='alert-message'>
                    <FormattedMessage {...FORM_ARE_YOU_SURE_TO_TEXT} />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage {...DELETE_THIS_CONNECTION_TEXT} />?
                    </span>
                    <br/>
                    <FormattedMessage {...ATTENDEE_WILL_NO_HAVE_UNIQUE_CONNECTION_TEXT} />
                </span>
            );

        this.alertsService.confirm({
            message: message,
            confirmButtonProps: {
                text: isPanelistChild ? <FormattedMessage {...DELETE_CONNECTION_TEXT} /> : <FormattedMessage {...YES_DELETE_TEXT} />,
                modifier: 'confirm-warn',
                handler: this.deleteChildConnection.bind(this, childConnection, parentConnectionId)
            },
            extendConfirmButtonProps: isPanelistChild && {
                text: <FormattedMessage {...DELETE_BOTH_TEXT} />,
                modifier: 'confirm-warn',
                handler: this.deleteChildConnection.bind(this, childConnection, parentConnectionId, true)
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private onChildConnectionUpdated(child: IChildConnectionDTO, parentConnectionId: number): void {
        this.meetingConnectionsDataFormVM.updateChildConnectionInParent(parentConnectionId, child);
        this.showConnectionUpdateNotification();
    }

    private showConnectionUpdateNotification(): void {
        this.alertsService.notification({ message: this.intl.formatMessage(UNIQUE_CONNECTION_DETAILS_UPDATED_TEXT) });
    }

    private getChildConnectionComponentByType(type: ConnectionType): (props: ChildConnectionFormProps) => JSX.Element {
        switch (type) {
            case ConnectionType.UniqueDialIn:
                return UniqueDialInChildConnectionForm;
            case ConnectionType.UniqueJoiningLink:
                return UniqueJoiningLinkChildConnectionForm;
            case ConnectionType.PanelistLink:
                return PanelistLinkChildConnectionForm;
            default:
                return () => null;
        }
    }

    private closeEditSubwindowById(id: number): void {
        this.subWindowMap[id]?.close();
    }

    private async deleteChildConnection(childConnection: IChildConnectionDTO, parentConnectionId: number, deleteZoomPanelist?: boolean): Promise<void> {
        try {
            const apiParams = {
                accountId: this.accountId,
                eventId: this.eventId,
                joinMethodId: childConnection.joinMethodId,
                panelistMeetingDelete: deleteZoomPanelist || false
            };
            await this.connectionsDAO.deleteChildConnection(apiParams);

            childConnection.type === ConnectionType.PanelistLink
                ? this.meetingConnectionsDataFormVM.removeChildConnectionById(parentConnectionId, childConnection.joinMethodId)
                : this.meetingConnectionsDataFormVM.deleteChildConnectionInParent(childConnection.joinMethodId, parentConnectionId);
            if (deleteZoomPanelist) {
                showNotification(panelistDeletionToastMessage);
            }
            this.closeEditSubwindowById(childConnection.joinMethodId);
            this.trackDeleteChildConnectionEvent(childConnection, this.meetingConnectionsDataFormVM.getMeetingConnectionById(parentConnectionId));
        } catch (err) {
            ChildConnectionsViewModel.handleApiException(err);
        }
    }

    private trackDeleteChildConnectionEvent(childConnection: IChildConnectionDTO, parentConnection: IConnectionDetailsDTO): void {
        let event: object = {
            'Join Method ID': childConnection.joinMethodId,
            'Attendee ID': childConnection.attendeeId,
            'Join Method Type': childConnection.type,
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getAccount().name,
            'Event ID': this.eventId,
            'Event Name': this.globalStorage.getEvent().name,
            'Meeting ID': this.meetingId
        };

        event = this.assignParentConnectionPropsToTrackingEvent(event, parentConnection);
        this.trackingService.get().track(TRACKING_EVENTS.DELETE_CONNECTION, event);
    }

    private assignParentConnectionPropsToTrackingEvent(event: object, connection: IConnectionDetailsDTO): object {
        switch (connection.type) {
            case ConnectionType.Zoom:
                return this.assignZoomConnectionPropsToTrackingEvent(event, connection);
            case ConnectionType.Webinar:
                return this.assignWebinarConnectionPropsToTrackingEvent(event, connection);
            case ConnectionType.AudioBridge:
                return this.assignAudioBridgeConnectionPropsToTrackingEvent(event, connection);
            default:
                return event;
        }
    }

    private assignZoomConnectionPropsToTrackingEvent(event: object, connection: IConnectionDetailsDTO): object {
        event['Zoom ID'] = connection.connectionId;
        return event;
    }

    private assignWebinarConnectionPropsToTrackingEvent(event: object, connection: IConnectionDetailsDTO): object {
        event['Webinar ID'] = connection.connectionId;
        return event;
    }

    private assignAudioBridgeConnectionPropsToTrackingEvent(event: object, connection: IConnectionDetailsDTO): object {
        event['Conference ID'] = connection.conferenceId;
        return event;
    }
}